import { FunctionComponent, useEffect, useState } from "react";
import { commafy } from "../utils/utils";
import {
  parseStoreHours,
  HourDetails,
  HoursByDay,
  daysOfTheWeek,
  createHourString,
} from "../utils/storeHours";
import "react-responsive-carousel/lib/styles/carousel.css";
import { STATES } from "../utils/states";
import { Link } from "gatsby";
import { StoreProductPivot, Store, Brands } from "../types.d.ts";
import filterProducts from "../utils/productFilter";
import StoreOpenClose from "./open-close";
import { Icon } from "../utils/Icons";
import { LocalBusiness, WithContext } from "schema-dts";
import { ThemeUICSSObject, ThemeUIStyleObject } from "theme-ui";
import { useSecureLinks } from "../../hooks/useSecureLinks";

interface Props {
  _openApp: Function;
  store: Store;
  brands?: Brands[];
  baseUrl: string;
}

const h3StylesCM: ThemeUICSSObject = {
  textTransform: "capitalize",
  fontFamily: "heading",
  color: "#000",
  fontWeight: "600",
  fontSize: "24px",
  width: "100%",
  borderBottom: "solid 2px #ccc",
  marginBottom: "10px",
};

const ConditionalLink: React.FC<{
  link: string | undefined;
  brands?: Brands[];
  children?: React.ReactNode;
}> = ({ link, brands, children }) => {
  const style = {
    width: "100%",
    paddingBottom: "15px",
    paddingTop: "15px",
    borderBottom: "none",
  } as ThemeUIStyleObject;
  if (link)
    return (
      <Link
        to={link}
        role="button"
        tabIndex={0}
        sx={{
          ...style,
          ":hover": {
            bg: "#fbfbfb",
          },
        }}
      >
        {children}
      </Link>
    );
  return <div sx={style}>{children}</div>;
};

const HoursSection: React.FC<HourDetails> = (props) => {
  const isOpen24Hours =
    (props.ranges || [])[0]?.deltas?.min == 0 &&
    (props.ranges || [])[0]?.deltas?.max == 10079;
  if (isOpen24Hours)
    return (
      <div>
        <strong>Open 24 hours</strong>
      </div>
    );
  return (
    <div>
      {(Object.values(daysOfTheWeek || []) as (keyof HoursByDay)[]).map(
        (day, i) => {
          const hours = props.days[day];
          return (
            <div
              key={i}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                height: "25px",
                color: "#000",
                fontWeight: i == new Date().getDay() ? "bold" : "normal",
              }}
            >
              <div
                sx={{
                  textAlign: "left",
                }}
              >
                <p
                  sx={{
                    marginTop: "0px",
                    marginBottom: "0px",
                  }}
                >
                  {day}
                </p>
              </div>
              <div
                sx={{
                  borderBottom: "1px dotted #ccc !important",
                  flex: 1,
                }}
              ></div>
              <div
                sx={{
                  textAlign: "right",
                }}
              >
                <p
                  sx={{
                    marginTop: "0px",
                    marginBottom: "0px",
                  }}
                >
                  {hours
                    ? `${createHourString(
                        hours.openHour,
                        hours.openMin
                      )}-${createHourString(hours.closeHour, hours.closeMin)}`
                    : "Closed"}
                </p>
              </div>
            </div>
          );
        }
      )}
    </div>
  );
};

const StoreDetailsSection: FunctionComponent<Props> = (props: Props) => {
  const { store } = props;
  const hoursDetailed =
    store.hours == "Coming Soon" ? null : parseStoreHours(store.hours ?? "");

  const h3Styles = h3StylesCM;

  const phoneNumber = (store.phone ?? "")
    .slice(2)
    .toString()
    .replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
  const faxNumber = (store.fax ?? "")
    .slice(2)
    .toString()
    .replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");

  //per province regulation, do not show shortapp for manitoba
  const hideShortApp = store?.city?.state?.name?.toLowerCase() == "manitoba";
  const secureLinks = useSecureLinks();

  return (
    <div
      sx={{
        width: "100%",
        height: "100%",
        position: "relative",
        borderLeft: "none",
        borderRight: "none",
        borderBottom: "none",
        borderTop: "1px solid #e8e8e8",
        "& > div": {
          height: "100%",
          width: "100%",
        },
      }}
    >
      <div
        sx={{
          height: "100%",
          color: "#000",
        }}
      >
        <div>
          <div
            sx={{
              textAlign: "center",
            }}
          >
            <div
              sx={{
                bg: store.cashless ? "brand.skyBlue" : "white",
                mt: !store.cashless ? "30px" : "-1px",
                py: "20px",
              }}
            >
              {store.cashless && <p>Cashless</p>}
              <h2
                sx={{
                  fontSize: "28px",
                  marginBottom: "5px",
                  fontWeight: "600",
                  fontFamily: "heading",
                }}
              >
                {store.crossStreets}
              </h2>
              {hoursDetailed && (
                <h3
                  sx={{
                    textTransform: "none",
                    borderBottom: "none",
                    marginTop: "0px",
                    fontWeight: "400 !important",
                    marginBottom: "16px",
                  }}
                >
                  <StoreOpenClose store={store} hoursDetailed={hoursDetailed} />
                </h3>
              )}
            </div>
            <div sx={{ px: ["20px", null, null, "58px"] }}>
              {store.flashAlert && (
                <div
                  sx={{
                    backgroundColor: "#ffcccc",
                    borderRadius: "10px",
                    marginRight: ["0", "0", "0", "50px"],
                    marginLeft: ["0", "0", "0", "50px"],
                    marginTop: [3],
                  }}
                >
                  <p
                    sx={{
                      fontSize: "18px",
                      paddingTop: "15px",
                      paddingBottom: "15px",
                      paddingRight: "20px",
                      paddingLeft: "20px",
                    }}
                    dangerouslySetInnerHTML={{ __html: store.flashAlert }}
                  ></p>
                </div>
              )}
            </div>

            <div
              sx={{
                marginBottom: "60px",
                px: ["20px", null, null, "58px"],
              }}
            >
              <div
                sx={{
                  display: "flex",
                  flexDirection: ["column", null, null, "row"],
                  justifyContent: ["center", null, null, "space-between"],
                  alignItems: "center",
                  marginBottom: "20px",
                  "& p": {
                    marginTop: "2.5px",
                    marginBottom: "2.5px",
                  },
                }}
              >
                <div
                  sx={{
                    marginTop: "10px",
                    mr: [0, null, null, 4],
                    fontSize: ["18px", null, null, "20px"],
                  }}
                >
                  <p
                    sx={{
                      whiteSpace: "nowrap",
                    }}
                  >
                    {store.address1}
                  </p>
                  <p
                    sx={{
                      whiteSpace: "nowrap",
                    }}
                  >
                    {store.city?.name +
                      ", " +
                      (STATES.find(
                        (state) => store.city?.state?.name == state.name
                      )?.abbr || "") +
                      " " +
                      store.zip}
                  </p>
                  <p
                    sx={{
                      whiteSpace: "nowrap",
                      fontSize: ["16px", null, null, "18px"],
                      fontWeight: "600",
                      display: "none",
                    }}
                  >
                    <a
                      sx={{
                        variant: "a.default",
                        display: "inline-block",
                        mt: [3, null, null, 0],
                        fontFamily: "heading",
                        fontSize: "1.125rem",
                      }}
                      href={
                        "https://maps.apple.com?saddr=Current+Location&daddr=@" +
                        store.gPS
                      }
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      Get Directions
                      <Icon
                        alt="External Link"
                        name="externalLinkAlt"
                        height={16}
                        width={16}
                        fill="brand.red"
                        sx={{
                          mx: 2,
                        }}
                      />
                    </a>
                  </p>
                </div>
                <div
                  sx={{
                    marginTop: "10px",
                    fontFamily: "heading",
                    fontSize: "1.125rem",
                  }}
                >
                  <p
                    sx={{
                      whiteSpace: "nowrap",
                    }}
                  >
                    {"Phone: "}
                    <a
                      sx={{ variant: "a.default" }}
                      href={"tel:" + store.phone?.slice(1)}
                    >
                      {phoneNumber}
                    </a>
                  </p>
                  <p
                    sx={{
                      whiteSpace: "nowrap",
                    }}
                  >
                    {"Fax: " + faxNumber}
                  </p>
                </div>
              </div>
              {!hideShortApp && (
                <div
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {/* <button
                    sx={{
                      variant: "buttons.primary",
                      py: "10px",
                      maxWidth: "400px",
                      mt: 2,
                      letterSpacing: ".5px",
                    }}
                    onClick={() => props._openApp()}
                  >
                    Get Started
                  </button> */}
                  <a
                    href={secureLinks.loanApplication}
                    sx={{
                      variant: "buttons.newPrimary",
                      py: "10px",
                      maxWidth: "250px",
                      mt: 2,
                      letterSpacing: ".5px",
                    }}
                  >
                    Get Started
                  </a>
                </div>
              )}
            </div>
          </div>
          <div>
            <div sx={{ px: ["20px", null, null, "58px"] }}>
              <h3 sx={h3Styles}>Hours</h3>
              <div
                sx={{
                  marginBottom: "60px",
                }}
              >
                {hoursDetailed ? (
                  <HoursSection {...(hoursDetailed as HourDetails)} />
                ) : (
                  <strong>Coming Soon</strong>
                )}
              </div>
              <h3 sx={{ ...h3Styles, mb: 0 }}>Services</h3>
              <div
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  "& > a > div:not(:last-child)": {
                    borderBottom: "solid 1px #ccc",
                  },
                }}
              >
                {store.products
                  .sort(
                    (a: StoreProductPivot, b: StoreProductPivot) =>
                      a.orderIndex - b.orderIndex
                  )
                  .map((product: StoreProductPivot) => {
                    const stateProduct = (product.stateProduct || [])[0];
                    let url =
                      stateProduct?.url ||
                      (store.brand == Brands.Cashmoney
                        ? product.product?.cashMoneyUrl
                        : product.product?.speedyCashUrl) ||
                      undefined;
                    if (url && !url?.startsWith("/")) url = "/" + url;

                    return (
                      <ConditionalLink
                        link={url}
                        key={product.product?.productName}
                        brands={props.brands}
                      >
                        <div
                          sx={{
                            width: "100%",
                            display: "flex",
                            flexShrink: 0,
                            minHeight: "50.4px",
                          }}
                        >
                          <div
                            sx={{
                              flexGrow: 10,
                              marginRight: "20px",
                            }}
                          >
                            <h5
                              sx={{
                                marginTop: "0px",
                                marginBottom: "0px",
                                fontWeight: "bold",
                                fontSize: "16px",
                                transition: "colors 0.3s ease-in",
                                ":hover": {
                                  color: "brand.red",
                                },
                              }}
                            >
                              {product.product?.productName}
                            </h5>
                            <p
                              sx={{
                                marginBottom: "0px",
                                marginTop: "0px",
                              }}
                            >
                              {stateProduct?.storeMaxAmount}
                            </p>
                          </div>
                          {url && (
                            <div
                              sx={{
                                textAlign: "center",
                              }}
                            >
                              <div
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                  display: "flex",
                                  flexShrink: 0,
                                  justifyContent: "center",
                                  alignItems: "flex-start",
                                  "& img": {
                                    width: "28px",
                                  },
                                }}
                              >
                                <div
                                  sx={{
                                    width: "100px",
                                    textAlign: "center",
                                    px: "10px",
                                    display: "flex",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    fontSize: "24px",
                                    "& svg": {
                                      cursor: "pointer",
                                      color: "#00502f",
                                    },
                                  }}
                                >
                                  <Icon
                                    alt="Store Details"
                                    name="infoCircle"
                                    height={24}
                                    width={24}
                                    viewBox="0 0 30 30"
                                    tabIndex={0}
                                    fill="brand.green"
                                    sx={{
                                      mt: 1,
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </ConditionalLink>
                    );
                  })}
              </div>
            </div>
            {store.cashless && (
              <div
                sx={{
                  display: "flex",
                  flexShrink: 0,
                  justifyContent: "space-between",
                  bg: "brand.skyBlue",
                  mx: [3, null, null, "50px"],
                  borderRadius: 10,
                  p: "30px",
                  py: "20px",
                  marginBottom: 4,
                }}
              >
                <div>
                  <strong>Services not available at Cashless Branches</strong>
                  <p>Only available at standard Cash Money branches</p>
                  <ul
                    sx={{
                      listStyle: "inside",
                      pt: 3,
                    }}
                  >
                    <li>Money Orders</li>
                    <li>Western Union® Money Transfers</li>
                    <li>Funding or Payments with Cash</li>
                  </ul>
                </div>
                <div
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "50px",
                  }}
                >
                  <Icon
                    alt="Store Details"
                    name="infoCircle"
                    height={24}
                    width={24}
                    viewBox="0 0 30 30"
                    tabIndex={0}
                    fill="brand.green"
                    sx={{
                      mt: 1,
                    }}
                  />
                </div>
              </div>
            )}
            <div sx={{ px: ["20px", null, null, "58px"] }}>
              <h3
                sx={{
                  ...h3Styles,
                }}
              >
                Directions
              </h3>
              <p
                sx={{
                  pb: 20,
                }}
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html: store.directionsText ?? "",
                  }}
                />
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoreDetailsSection;
